// DEVAMS-2070: Aircraft E-Handover store — Options API style to match project convention.
// Handles all API communication for /api/v1/aircraft-handover/* endpoints.
// DEVAMS-2082: Mercure SSE subscription for real-time handover updates.
import { defineStore } from 'pinia'
import jwtInterceptor from '@/plugins/jwtInterceptor'
import * as Sentry from '@sentry/vue'
import { useMercure, type MercureHandle } from '@/composables/useMercure'
import type {
  AircraftHandover,
  AircraftHandoverItem,
  AircraftHandoverItemImage,
} from '@/types/aircraftHandover'

type AxiosErrorLike = { response?: { status?: number } }

// DEVAMS-2082: The MercureHandle is non-serializable (holds an EventSource reference),
// so it cannot live inside Pinia's reactive state. A module-level variable is the
// idiomatic Options API approach — it is store-scoped since this module is a singleton
// (Vite HMR preserves module scope, and Pinia stores are singletons per app instance).
let _mercureHandle: MercureHandle | null = null
// When the current user triggers a mutation, we increment this counter so that the
// resulting Mercure echoes are ignored rather than showing a self-reload banner.
// Each save/issue/note generates 2 Mercure events (entity listener postUpdate + explicit
// service publish), so those actions suppress 2. Create generates 1 (entity listener
// postPersist only). The 5-second timeout is a safety valve in case an event never arrives.
let _suppressMercureCount = 0
let _suppressResetTimer: ReturnType<typeof setTimeout> | null = null

function suppressNextMercureUpdate(count: number = 1): void {
  _suppressMercureCount += count
  if (_suppressResetTimer) clearTimeout(_suppressResetTimer)
  _suppressResetTimer = setTimeout(() => {
    _suppressMercureCount = 0
    _suppressResetTimer = null
  }, 5000)
}

export const useAircraftHandoverStore = defineStore('aircraftHandover', {
  state: (): {
    handovers: AircraftHandover[]
    loading: boolean
    saving: boolean
    signing: boolean
    error: string | null
    mercureToken: string | null
    mercureTopic: string | null
    staleUpdate: { updatedBy: string; updatedAt: string; action: string } | null
  } => ({
    handovers: [],
    loading: false,
    saving: false,
    signing: false,
    error: null,
    mercureToken: null,
    mercureTopic: null,
    staleUpdate: null,
  }),

  getters: {
    /**
     * Returns the single editable draft, or null if none exists.
     * Only one draft is allowed per task at a time (enforced by the backend).
     */
    draftHandover(state): AircraftHandover | null {
      return state.handovers.find((h) => h.status === 'draft') ?? null
    },

    /**
     * Returns the issued (but not yet noted) handover, or null if none exists.
     * An issued handover must be noted before a new draft can be started.
     */
    issuedHandover(state): AircraftHandover | null {
      return state.handovers.find((h) => h.status === 'issued') ?? null
    },

    /**
     * Returns all fully completed (noted) handovers sorted by creation date descending.
     * These are the historical records shown in the history dialog.
     */
    signedHandovers(state): AircraftHandover[] {
      return [...state.handovers.filter((h) => h.status === 'noted')].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
    },
  },

  actions: {
    /**
     * Fetches all handovers for a given planned-work task.
     * GET /api/v1/aircraft-handover/task/{plannedWorkId}
     */
    async fetchHandovers(plannedWorkId: number): Promise<void> {
      this.loading = true
      this.error = null
      this.staleUpdate = null
      try {
        const response = await jwtInterceptor.get<AircraftHandover[]>(
          `v1/aircraft-handover/task/${plannedWorkId}`,
        )
        this.handovers = response?.data ?? []
        // DEVAMS-2082: Capture Mercure subscriber credentials from response headers.
        // The backend only emits these headers on the first call (or after token expiry),
        // so we only overwrite the stored values when the headers are actually present.
        const token = response.headers?.['x-mercure-token']
        const topic = response.headers?.['x-mercure-topic']
        if (token) this.mercureToken = token
        if (topic) this.mercureTopic = topic
      } catch (error) {
        this.error = 'Failed to load handovers'
        Sentry.captureException(error, {
          tags: { store: 'aircraftHandover', action: 'fetchHandovers', plannedWorkId },
        })
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Creates a new draft handover (optionally copying items from the latest one).
     * POST /api/v1/aircraft-handover
     */
    async createDraft(
      plannedWorkId: number,
      copyFromLatest: boolean = false,
    ): Promise<AircraftHandover | null> {
      this.saving = true
      this.error = null
      suppressNextMercureUpdate(1) // entity listener postPersist only — service has no explicit publish for create
      try {
        const response = await jwtInterceptor.post<AircraftHandover>('v1/aircraft-handover', {
          plannedWorkId,
          copyFromLatest,
        })
        const created: AircraftHandover = response.data
        this.handovers = [...this.handovers, created]
        return created
      } catch (error) {
        const status = (error as AxiosErrorLike)?.response?.status
        if (status === 409) {
          const message: string = (error as any)?.response?.data?.message ?? ''
          if (message.toLowerCase().includes('draft already exists')) {
            // A draft already exists — silently reload so the UI shows the pre-existing draft.
            await this.fetchHandovers(plannedWorkId)
            return null
          }
          // Any other 409 (e.g. "pending acknowledgement") — surface the message to the caller.
          throw error
        }
        this.error = 'Failed to create handover draft'
        Sentry.captureException(error, {
          tags: { store: 'aircraftHandover', action: 'createDraft', plannedWorkId },
        })
        throw error
      } finally {
        this.saving = false
      }
    },

    /**
     * Saves header fields and items for a draft handover.
     * PUT /api/v1/aircraft-handover/{handoverId}
     */
    async saveItems(
      handoverId: number,
      headerFields: Partial<AircraftHandover>,
      items: AircraftHandoverItem[],
    ): Promise<AircraftHandover> {
      this.saving = true
      this.error = null
      suppressNextMercureUpdate(2) // entity listener postUpdate + explicit service publish
      try {
        const response = await jwtInterceptor.put<AircraftHandover>(
          `v1/aircraft-handover/${handoverId}`,
          { ...headerFields, items },
        )
        const updated: AircraftHandover = response.data
        this.handovers = this.handovers.map((h) => (h.id === handoverId ? updated : h))
        return updated
      } catch (error) {
        this.error = 'Failed to save handover'
        Sentry.captureException(error, {
          tags: { store: 'aircraftHandover', action: 'saveItems', handoverId },
        })
        throw error
      } finally {
        this.saving = false
      }
    },

    /**
     * Issues the handover (first e-sign, transitions draft → issued, triggers PDF generation).
     * POST /api/v1/aircraft-handover/{handoverId}/issue
     * Returns 401 when the e-sign password is incorrect.
     */
    async issueHandover(handoverId: number, password: string): Promise<AircraftHandover> {
      this.signing = true
      this.error = null
      suppressNextMercureUpdate(2) // entity listener postUpdate + explicit service publish
      try {
        const response = await jwtInterceptor.post<AircraftHandover>(
          `v1/aircraft-handover/${handoverId}/issue`,
          { password },
        )
        const updated: AircraftHandover = response.data
        this.handovers = this.handovers.map((h) => (h.id === handoverId ? updated : h))
        return updated
      } catch (error) {
        const status = (error as AxiosErrorLike)?.response?.status
        if (status !== 401) {
          // 401 is handled in the component to show "incorrect password" feedback.
          Sentry.captureException(error, {
            tags: { store: 'aircraftHandover', action: 'issueHandover', handoverId },
          })
        }
        throw error
      } finally {
        this.signing = false
      }
    },

    /**
     * Notes the handover (second e-sign, transitions issued → noted).
     * POST /api/v1/aircraft-handover/{handoverId}/note
     * Returns 401 when the e-sign password is incorrect.
     */
    async noteHandover(handoverId: number, password: string): Promise<AircraftHandover> {
      this.signing = true
      this.error = null
      suppressNextMercureUpdate(2) // entity listener postUpdate + explicit service publish
      try {
        const response = await jwtInterceptor.post<AircraftHandover>(
          `v1/aircraft-handover/${handoverId}/note`,
          { password },
        )
        const updated: AircraftHandover = response.data
        this.handovers = this.handovers.map((h) => (h.id === handoverId ? updated : h))
        return updated
      } catch (error) {
        const status = (error as AxiosErrorLike)?.response?.status
        if (status !== 401) {
          Sentry.captureException(error, {
            tags: { store: 'aircraftHandover', action: 'noteHandover', handoverId },
          })
        }
        throw error
      } finally {
        this.signing = false
      }
    },

    /**
     * Uploads an annotated image for a handover item.
     * POST /api/v1/aircraft-handover/{handoverId}/item/{itemId}/image
     * Accepts multipart/form-data with field "image".
     */
    async uploadImage(
      handoverId: number,
      itemId: number,
      file: File | Blob,
      filename: string,
    ): Promise<AircraftHandoverItemImage> {
      try {
        const formData = new FormData()
        formData.append('image', file, filename)
        const response = await jwtInterceptor.post<AircraftHandoverItemImage>(
          `v1/aircraft-handover/${handoverId}/item/${itemId}/image`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        const newImage: AircraftHandoverItemImage = response.data

        // Optimistically push the new image into the cached handover item.
        this.handovers = this.handovers.map((h) => {
          if (h.id !== handoverId) return h
          return {
            ...h,
            items: h.items.map((item) => {
              if (item.id !== itemId) return item
              return { ...item, images: [...item.images, newImage] }
            }),
          }
        })

        return newImage
      } catch (error) {
        Sentry.captureException(error, {
          tags: { store: 'aircraftHandover', action: 'uploadImage', handoverId, itemId },
        })
        throw error
      }
    },

    /**
     * Deletes an image from a handover item.
     * DELETE /api/v1/aircraft-handover/{handoverId}/item/{itemId}/image/{imageId}
     */
    async deleteImage(handoverId: number, itemId: number, imageId: number): Promise<void> {
      try {
        await jwtInterceptor.delete(
          `v1/aircraft-handover/${handoverId}/item/${itemId}/image/${imageId}`,
        )
        // Remove the image from the cached state immediately.
        this.handovers = this.handovers.map((h) => {
          if (h.id !== handoverId) return h
          return {
            ...h,
            items: h.items.map((item) => {
              if (item.id !== itemId) return item
              return { ...item, images: item.images.filter((img) => img.id !== imageId) }
            }),
          }
        })
      } catch (error) {
        Sentry.captureException(error, {
          tags: { store: 'aircraftHandover', action: 'deleteImage', handoverId, itemId, imageId },
        })
        throw error
      }
    },

    /**
     * Deletes a draft handover.
     * DELETE /api/v1/aircraft-handover/{handoverId}
     * Only allowed when the handover is in draft status (enforced by the backend).
     */
    async deleteDraft(handoverId: number): Promise<void> {
      this.saving = true
      this.error = null
      try {
        await jwtInterceptor.delete(`v1/aircraft-handover/${handoverId}`)
        this.handovers = this.handovers.filter((h) => h.id !== handoverId)
      } catch (error) {
        this.error = 'Failed to delete handover draft'
        Sentry.captureException(error, {
          tags: { store: 'aircraftHandover', action: 'deleteDraft', handoverId },
        })
        throw error
      } finally {
        this.saving = false
      }
    },

    /**
     * Clears the stale-handover notification and reloads the handover list.
     * Called when the user manually acknowledges the "handover changed" banner.
     */
    async clearStale(plannedWorkId: number): Promise<void> {
      this.staleUpdate = null
      await this.fetchHandovers(plannedWorkId)
    },

    /**
     * Opens a Mercure SSE subscription for real-time handover updates.
     * DEVAMS-2082: Must be called after fetchHandovers() so that mercureToken
     * and mercureTopic are available. Closes any existing connection first to
     * prevent duplicate subscriptions (e.g. navigating back to the same page).
     */
    startMercure(plannedWorkId: number): void {
      // Always close any pre-existing connection before opening a new one.
      this.stopMercure()

      const token = this.mercureToken
      const topic = this.mercureTopic
      if (!token || !topic) {
        console.warn('[aircraftHandoverStore] startMercure: no token or topic — skipping SSE subscription.')
        return
      }

      _mercureHandle = useMercure({
        topic,
        token,
        onMessage: (data: unknown) => {
          if (_suppressMercureCount > 0) {
            _suppressMercureCount--
            return
          }
          const payload = data as { action?: string; updatedBy?: string; updatedAt?: string }
          this.staleUpdate = {
            updatedBy: payload.updatedBy ?? 'Someone',
            updatedAt: payload.updatedAt ?? '',
            action: payload.action ?? 'updated',
          }
        },
        onTokenExpired: async () => {
          // Re-fetch to obtain a fresh subscriber JWT from the backend headers.
          await this.fetchHandovers(plannedWorkId)
          return this.mercureToken ?? undefined
        },
      })
      _mercureHandle.connect()
    },

    /**
     * Closes the active Mercure SSE connection and releases the handle.
     * Safe to call even when no connection is open.
     */
    stopMercure(): void {
      _mercureHandle?.disconnect()
      _mercureHandle = null
    },
  },
})
